import ArrowDownwardIcon from '../../../assets/icons/arrow_downward.svg?react';
import ArrowUpwardIcon from '../../../assets/icons/arrow_upward.svg?react';
import SwapVertIcon from '../../../assets/icons/swap_vert.svg?react';
import { cn } from '../../../utils/className';
import { Checkbox } from '../../Checkbox';
import { CompactTableColumn, CompactTableSort } from './types';

interface CompactTableProps<TData> {
  data: TData[];
  columns: CompactTableColumn<TData>[];
  getRowId: (item: TData) => string;
  getRowLabel?: (item: TData) => string;
  selectionMode: boolean;
  selectedIds: string[];
  onSelect: (id: string) => void;
  onItemClick: (item: TData) => void;
  activeId?: string;
  sort?: CompactTableSort;
}

/**
 * Rows and columns for the tablet. It carries the columns the desktop table
 * shows, without the hover-only resize handles and row actions a finger cannot
 * reach.
 */
export function CompactTable<TData>({
  data,
  columns,
  getRowId,
  getRowLabel,
  selectionMode,
  selectedIds,
  onSelect,
  onItemClick,
  activeId,
  sort,
}: CompactTableProps<TData>) {
  return (
    <table className='w-full table-fixed border-collapse text-left'>
      <thead
        className='bg-neutral-050 sticky top-0 z-10
          shadow-[0_1px_0_0_var(--color-border-secondary)]'
      >
        <tr>
          {selectionMode && <th scope='col' className='w-12 px-4' />}

          {columns.map((column) => (
            <th
              key={column.id}
              scope='col'
              aria-sort={ariaSort(column, sort)}
              className={cn(
                `size-caption font-regular text-secondary px-4 py-2 align-middle
                whitespace-nowrap`,
                '[&:not(:last-child)]:[box-shadow:inset_-1px_0_0_0_var(--color-border-secondary)]',
                column.className
              )}
            >
              {column.sortable && sort ? (
                <SortHeader column={column} sort={sort} />
              ) : (
                column.header
              )}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {data.map((item) => {
          const id = getRowId(item);
          const isSelected = selectedIds.includes(id);

          return (
            <tr
              key={id}
              tabIndex={0}
              aria-current={id === activeId ? 'true' : undefined}
              data-state={isSelected ? 'selected' : undefined}
              onClick={() => onItemClick(item)}
              onKeyDown={(event) => {
                // A key pressed on the checkbox belongs to the checkbox.
                if (event.target !== event.currentTarget) return;
                if (event.key !== 'Enter' && event.key !== ' ') return;

                event.preventDefault();
                onItemClick(item);
              }}
              className={cn(
                `border-border-secondary cursor-pointer border-b
                transition-colors`,
                'hover:bg-surface-primary-support',
                `focus-visible:ring-focus focus-visible:ring-2
                focus-visible:outline-none`,
                id === activeId && 'bg-surface-focus-support',
                isSelected && 'bg-surface-focus-support'
              )}
            >
              {selectionMode && (
                <td className='px-4 py-4 align-top'>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onSelect(id)}
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`Select ${getRowLabel?.(item) ?? id}`}
                    className='h-5 w-5'
                  />
                </td>
              )}

              {columns.map((column) => (
                <td
                  key={column.id}
                  className={cn(
                    'size-p2 text-secondary px-4 py-4 align-top',
                    column.className
                  )}
                >
                  {column.cell(item)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ariaSort<TData>(
  column: CompactTableColumn<TData>,
  sort: CompactTableSort | undefined
): 'ascending' | 'descending' | 'none' | undefined {
  if (!column.sortable || !sort) return undefined;
  if (sort.columnId !== column.id) return 'none';

  return sort.descending ? 'descending' : 'ascending';
}

interface SortHeaderProps<TData> {
  column: CompactTableColumn<TData>;
  sort: CompactTableSort;
}

/**
 * The whole header is the control, so a finger has the full cell to hit. The
 * cycle matches the desktop table: ascending, descending, then unsorted.
 */
function SortHeader<TData>({ column, sort }: SortHeaderProps<TData>) {
  const active = sort.columnId === column.id;

  const advance = () => {
    if (!active) return sort.onSortChange(column.id, 'asc');
    if (!sort.descending) return sort.onSortChange(column.id, 'desc');

    return sort.onSortChange(column.id, null);
  };

  return (
    <button
      type='button'
      onClick={advance}
      className='focus-visible:outline-focus flex w-full min-w-0 cursor-pointer
        items-center justify-between gap-1 rounded py-0.5 focus-visible:outline'
    >
      <span className='min-w-0 truncate'>{column.header}</span>

      {!active && (
        <SwapVertIcon
          aria-hidden
          className='text-text-tertiary h-4 w-4 shrink-0 opacity-60
            [&_path]:fill-current'
        />
      )}
      {active && !sort.descending && (
        <ArrowUpwardIcon
          aria-hidden
          className='text-surface-focus h-4 w-4 shrink-0 [&_path]:fill-current'
        />
      )}
      {active && sort.descending && (
        <ArrowDownwardIcon
          aria-hidden
          className='text-surface-focus h-4 w-4 shrink-0 [&_path]:fill-current'
        />
      )}
    </button>
  );
}
